import React from 'react';
import { Box, Typography, Autocomplete, TextField } from '@mui/material';
import { colors, fonts, borderRadius } from '../../../constants';
import EditQueryFieldLabel from './EditQueryFieldLabel';

/**
 * EditQueryAutocompleteField Component
 * Reusable autocomplete field for EditQueryModal
 * 
 * @param {string} label - Field label
 * @param {boolean} required - Whether field is required
 * @param {Array} options - Options array
 * @param {*} value - Selected value
 * @param {Function} onChange - Change handler
 * @param {Function} onFocus - Focus handler
 * @param {Function} onBlur - Blur handler
 * @param {Function} getOptionLabel - Function to get option label
 * @param {Function} isOptionEqualToValue - Function to compare options
 * @param {boolean} disabled - Whether field is disabled
 * @param {boolean} loading - Whether options are loading
 * @param {boolean} hasError - Whether field has error
 * @param {boolean} isFocused - Whether field is focused
 * @param {string} placeholder - Placeholder text
 * @param {string} errorMessage - Error message to display
 * @param {string} helperText - Helper text to display
 * @param {string} title - Title attribute for tooltip
 * @param {Function} filterOptions - Custom filter function
 */
const EditQueryAutocompleteField = ({
  label,
  required = false,
  options = [],
  value,
  onChange,
  onFocus,
  onBlur,
  getOptionLabel,
  isOptionEqualToValue,
  disabled = false,
  loading = false,
  hasError = false,
  isFocused = false,
  placeholder = '',
  errorMessage = '',
  helperText = '',
  title = '',
  filterOptions,
}) => {
  const getSelectStyle = () => {
    if (disabled) {
      return {
        width: '100%',
        padding: '10px 12px',
        fontSize: fonts.fontSizeSm,
        fontFamily: fonts.fontFamily,
        background: colors.backgroundSoft,
        borderRadius: borderRadius.input,
        border: `1px solid ${colors.borderHover}`,
        minHeight: '36px',
        lineHeight: 1.4,
        boxSizing: 'border-box',
        opacity: 0.6,
        cursor: 'not-allowed',
      };
    }

    return {
      width: '100%',
      padding: '10px 12px',
      fontSize: fonts.fontSizeSm,
      fontFamily: fonts.fontFamily,
      background: 'linear-gradient(135deg, #ffffff 0%, #f7faff 100%)',
      borderRadius: borderRadius.input,
      border: isFocused
        ? `2px solid ${hasError ? colors.error : '#0e172a'}`
        : `1.5px solid ${hasError ? colors.error : colors.borderDefault}`,
      minHeight: '36px',
      lineHeight: 1.4,
      transition: 'all 0.2s ease-in-out',
      outline: 'none',
      cursor: 'pointer',
      boxSizing: 'border-box',
      boxShadow: isFocused
        ? '0 0 0 4px rgba(66, 133, 244, 0.12)'
        : 'inset 0 1px 3px rgba(15, 63, 128, 0.08)',
    };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {label && <EditQueryFieldLabel label={label} required={required} />}
      <Autocomplete
        options={options}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        loading={loading}
        openOnFocus
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              readOnly: false,
              autoComplete: 'off',
            }}
            placeholder={placeholder}
            error={hasError}
            title={title}
            sx={{
              '& .MuiOutlinedInput-root': {
                ...getSelectStyle(),
                padding: '4px 9px',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                },
              },
              '& .MuiInputBase-input': {
                padding: '8px 4px',
                fontSize: fonts.fontSizeSm,
                cursor: disabled || loading ? 'not-allowed' : 'text',
              },
            }}
          />
        )}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-inputRoot': {
            padding: '0 !important',
          },
        }}
      />
      {(errorMessage || helperText) && (
        <Typography
          variant="caption"
          sx={{
            color: errorMessage ? colors.error : colors.textSecondary,
            mt: '2px',
            fontSize: fonts.fontSizeXs,
            display: 'block',
          }}
        >
          {errorMessage || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default EditQueryAutocompleteField;
